/**
 * @component toolbar-overflow-actions
 * @css toolbar-overflow-actions.css
 * @primitives btn, toolbar-action-bar
 */
import { createPopupMenuPortal } from '../utils/popup-menu-portal.js';

class ToolbarOverflowActions extends HTMLElement {
  constructor() {
    super();
    this.isMenuOpen = false;
    this._popupPortal = null;
    this._resizeObserver = null;
    this._layoutFrame = null;
    this._escapeHandler = null;
    this._isCollapsed = false;
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
    this.scheduleLayout();
  }

  disconnectedCallback() {
    this._popupPortal?.close();
    this._resizeObserver?.disconnect();
    if (this._layoutFrame) {
      cancelAnimationFrame(this._layoutFrame);
      this._layoutFrame = null;
    }
    if (this._escapeHandler) {
      document.removeEventListener('keydown', this._escapeHandler);
      this._escapeHandler = null;
    }
  }

  render() {
    if (this.querySelector('.toolbar-overflow-actions-row')) {
      return;
    }

    const row = document.createElement('div');
    row.className = 'toolbar-overflow-actions-row';
    row.innerHTML = `
        <div class="toolbar-overflow-inline">
          <slot name="overflow"></slot>
        </div>
        <div class="toolbar-overflow-trailing">
          <div class="toolbar-overflow-primary">
            <slot name="primary"></slot>
          </div>
          <div class="toolbar-overflow-menu-wrap" hidden>
            <button
              type="button"
              class="btn btn-secondary toolbar-overflow-trigger"
              aria-haspopup="menu"
              aria-expanded="false"
              aria-label="More actions"
            >
              <span class="toolbar-overflow-trigger-icon" aria-hidden="true">☰</span>
            </button>
            <ul class="toolbar-overflow-menu" role="menu" aria-label="More actions"></ul>
          </div>
        </div>
    `;
    this.appendChild(row);
  }

  attachEventListeners() {
    const primarySlot = this.querySelector('slot[name="primary"]');
    const overflowSlot = this.querySelector('slot[name="overflow"]');

    primarySlot?.addEventListener('slotchange', () => this.scheduleLayout());
    overflowSlot?.addEventListener('slotchange', () => this.scheduleLayout());

    if (!this._popupPortal) {
      this._popupPortal = createPopupMenuPortal(this, {
        openClass: 'open',
        align: 'right',
        gap: 8,
        onClose: () => {
          this.isMenuOpen = false;
          this.updateMenuState();
        }
      });
    }

    this.addEventListener('click', (e) => {
      if (e.target.closest('.toolbar-overflow-trigger')) {
        e.stopPropagation();
        this.toggleMenu();
      }
    });

    this.addEventListener('keydown', (e) => {
      if (!e.target.closest('.toolbar-overflow-trigger')) {
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggleMenu();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.openMenu();
        this.focusMenuItem(0);
      }
    });

    this._escapeHandler = (e) => {
      if (e.key === 'Escape' && this.isMenuOpen) {
        this.closeMenu();
        this.querySelector('.toolbar-overflow-trigger')?.focus();
      }
    };
    document.addEventListener('keydown', this._escapeHandler);

    if (typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(() => this.scheduleLayout());
      this._resizeObserver.observe(this);
    } else {
      window.addEventListener('resize', () => this.scheduleLayout());
    }
  }

  scheduleLayout() {
    if (this._layoutFrame) {
      cancelAnimationFrame(this._layoutFrame);
    }
    this._layoutFrame = requestAnimationFrame(() => {
      this._layoutFrame = null;
      this.layout();
    });
  }

  getSlottedButtons(slotName) {
    const slot = this.querySelector(`slot[name="${slotName}"]`);
    const assigned = slot?.assignedElements?.() ?? [];
    const elements = assigned.length
      ? assigned
      : Array.from(this.querySelectorAll(`[slot="${slotName}"]`));
    return elements.filter((el) => el instanceof HTMLElement);
  }

  layout() {
    const overflowButtons = this.getSlottedButtons('overflow');
    const menuWrap = this.querySelector('.toolbar-overflow-menu-wrap');

    if (!overflowButtons.length) {
      if (menuWrap) {
        menuWrap.hidden = true;
      }
      this.dataset.collapsed = 'false';
      this._isCollapsed = false;
      this.closeMenu();
      return;
    }

    overflowButtons.forEach((button) => {
      button.hidden = true;
    });
    if (menuWrap) {
      menuWrap.hidden = false;
    }
    this.dataset.collapsed = 'true';
    this._isCollapsed = true;
    this.rebuildMenu(overflowButtons);
  }

  rebuildMenu(overflowButtons) {
    const menu = this.querySelector('.toolbar-overflow-menu');
    if (!menu) {
      return;
    }

    menu.replaceChildren();
    overflowButtons.forEach((button, index) => {
      const item = document.createElement('li');
      item.setAttribute('role', 'menuitem');
      item.tabIndex = -1;
      item.className = 'toolbar-overflow-menu-item';
      item.dataset.index = String(index);
      item.innerHTML = button.innerHTML;

      item.addEventListener('click', (e) => {
        e.stopPropagation();
        button.click();
        this.closeMenu();
      });

      item.addEventListener('keydown', (e) => {
        const items = this.getMenuItems();
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          button.click();
          this.closeMenu();
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.focusMenuItem((index + 1) % items.length);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          this.focusMenuItem((index - 1 + items.length) % items.length);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          this.closeMenu();
          this.querySelector('.toolbar-overflow-trigger')?.focus();
        }
      });

      menu.appendChild(item);
    });
  }

  getMenu() {
    return this._popupPortal?.getMenu() ?? this.querySelector('.toolbar-overflow-menu');
  }

  getMenuItems() {
    const menu = this.getMenu();
    return menu ? Array.from(menu.querySelectorAll('.toolbar-overflow-menu-item')) : [];
  }

  toggleMenu() {
    if (this.isMenuOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  openMenu() {
    const menuWrap = this.querySelector('.toolbar-overflow-menu-wrap');
    const trigger = this.querySelector('.toolbar-overflow-trigger');
    const menu = this.querySelector('.toolbar-overflow-menu');
    if (menuWrap?.hidden || !trigger || !menu || !this._popupPortal) {
      return;
    }
    this.isMenuOpen = true;
    this._popupPortal.open(trigger, menu);
    this.updateMenuState();
  }

  closeMenu() {
    if (!this.isMenuOpen) {
      return;
    }
    this._popupPortal?.close();
  }

  updateMenuState() {
    const trigger = this.querySelector('.toolbar-overflow-trigger');
    const menu = this.getMenu();
    trigger?.setAttribute('aria-expanded', String(this.isMenuOpen));
    menu?.classList.toggle('open', this.isMenuOpen);
  }

  focusMenuItem(index) {
    const items = this.getMenuItems();
    if (index >= 0 && index < items.length) {
      items[index].focus({ preventScroll: true });
    }
  }
}

customElements.define('toolbar-overflow-actions', ToolbarOverflowActions);
